import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileCheck2, FileWarning, Upload } from "lucide-react";
import api, { blobErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// PART C (C4) + FIX-UP (Fix 2d): every order from dispatch onwards, filterable, and a POD can be
// attached straight from the row that is missing one.
export default function PODReportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", missing_only: true,
    status: "", channel: "", customer: "", search: "" });
  const [channels, setChannels] = useState([]);
  const [uploadFor, setUploadFor] = useState("");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/pod/report", { params: filters });
      setData(resp);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the POD report");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/channels").then(r => setChannels(r.data || [])).catch(() => {});
  }, []);

  const download = async () => {
    try {
      const res = await api.get("/pod/report/export", { params: filters, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `POD_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(await blobErrorMessage(e) || "Export failed");
    }
  };

  const startUpload = (orderId) => { setUploadFor(orderId); inputRef.current?.click(); };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadFor) return;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: resp } = await api.post(`/pod/orders/${uploadFor}/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      const before = Math.round((resp.size_before_bytes || 0) / 1024);
      const after = Math.round((resp.size_after_bytes || 0) / 1024);
      toast.success(before > after ? `POD attached — ${before} KB shrunk to ${after} KB`
                                   : "POD attached");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "The POD could not be attached");
    } finally {
      setUploadFor("");
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const rows = data?.rows || [];
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  return (
    <div className="p-6" data-testid="pod-report-page">
      <input ref={inputRef} type="file" className="hidden" onChange={pick}
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" data-testid="pod-row-file-input" />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Proof of Delivery</h1>
          <p className="text-xs text-zinc-400">Dispatched and delivered orders, and the signed copy on file{data ? ` · as of ${data.as_of}` : ""}</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={download} data-testid="pod-export-btn">
          <Download className="w-3.5 h-3.5 mr-1" /> Excel
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="pod-cards">
        <div className="border border-zinc-200 p-3 rounded-sm">
          <p className="text-[10px] text-zinc-400 uppercase">Orders in scope</p>
          <p className="text-lg font-mono font-bold" data-testid="pod-card-delivered">{data?.orders_in_scope ?? 0}</p>
          <p className="text-[10px] text-zinc-400">dispatched or delivered</p>
        </div>
        <div className="border border-emerald-200 bg-emerald-50 p-3 rounded-sm">
          <p className="text-[10px] text-emerald-700 uppercase flex items-center gap-1"><FileCheck2 className="w-3 h-3" /> POD on file</p>
          <p className="text-lg font-mono font-bold text-emerald-900" data-testid="pod-card-with">{data?.with_pod ?? 0}</p>
        </div>
        <div className="border border-red-200 bg-red-50 p-3 rounded-sm">
          <p className="text-[10px] text-red-700 uppercase flex items-center gap-1"><FileWarning className="w-3 h-3" /> No POD</p>
          <p className="text-lg font-mono font-bold text-red-900" data-testid="pod-card-without">{data?.without_pod ?? 0}</p>
          <p className="text-[10px] text-red-700">{data?.over_7_days_without_pod ?? 0} for more than 7 days</p>
        </div>
        <div className="border border-zinc-200 p-3 rounded-sm">
          <p className="text-[10px] text-zinc-400 uppercase">Invoice value without a POD</p>
          <p className="text-lg font-mono font-bold" data-testid="pod-card-value">{money(data?.missing_invoice_value)}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 border border-zinc-200 rounded-sm p-3">
        <div>
          <Label className="text-[10px] text-zinc-500 uppercase">Dispatched / delivered from</Label>
          <Input type="date" value={filters.date_from} onChange={e => set("date_from", e.target.value)}
            className="h-8 text-xs mt-1 rounded-sm" data-testid="pod-date-from" />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500 uppercase">to</Label>
          <Input type="date" value={filters.date_to} onChange={e => set("date_to", e.target.value)}
            className="h-8 text-xs mt-1 rounded-sm" data-testid="pod-date-to" />
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500 uppercase">Status</Label>
          <select value={filters.status} onChange={e => set("status", e.target.value)}
            className="h-8 text-xs mt-1 rounded-sm border border-zinc-300 px-2 bg-white block"
            data-testid="pod-status-filter">
            <option value="">All dispatched &amp; delivered</option>
            {(data?.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500 uppercase">Channel</Label>
          <select value={filters.channel} onChange={e => set("channel", e.target.value)}
            className="h-8 text-xs mt-1 rounded-sm border border-zinc-300 px-2 bg-white block"
            data-testid="pod-channel-filter">
            <option value="">All channels</option>
            {channels.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500 uppercase">Search</Label>
          <Input value={filters.search} onChange={e => set("search", e.target.value)}
            placeholder="order, customer or PO" className="h-8 text-xs mt-1 rounded-sm w-56"
            data-testid="pod-search" />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-zinc-600 h-8">
          <input type="checkbox" checked={filters.missing_only}
            onChange={e => set("missing_only", e.target.checked)}
            data-testid="pod-missing-only" />
          Show only orders with no POD
        </label>
      </div>

      {loading ? <p className="text-xs text-zinc-400">Loading…</p> : (
        <div className="border border-zinc-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="pod-table">
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                {["Order", "Customer", "Channel", "Status", "Dispatched", "Days since dispatch",
                  "Delivered", "Days since delivery", "GRN / Docket", "Invoice", "Value", "POD",
                  ""].map(h => (
                  <th key={h} className="text-left font-medium px-2 py-1.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.order_id} className="border-t border-zinc-100" data-testid={`pod-row-${r.order_number}`}>
                  <td className="px-2 py-1.5 font-mono">
                    <a className="text-blue-700 underline" href={`/orders/${r.order_id}`} target="_blank" rel="noreferrer">{r.order_number}</a>
                  </td>
                  <td className="px-2 py-1.5">{r.customer_name}</td>
                  <td className="px-2 py-1.5">{r.channel}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap" data-testid={`pod-status-${r.order_number}`}>{r.status}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.dispatch_date_display}</td>
                  <td className={`px-2 py-1.5 font-mono ${r.pod_count === 0 && r.days_since_dispatch > 7 ? "text-red-700 font-semibold" : ""}`}
                    data-testid={`pod-days-dispatch-${r.order_number}`}>{r.days_since_dispatch}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.delivery_date_display}</td>
                  <td className="px-2 py-1.5 font-mono">{r.days_since_delivery}</td>
                  <td className="px-2 py-1.5">{r.grn_number}</td>
                  <td className="px-2 py-1.5">{r.invoice_number}</td>
                  <td className="px-2 py-1.5 font-mono text-right">{money(r.invoice_value)}</td>
                  <td className="px-2 py-1.5">
                    {r.pod_count > 0
                      ? <span className="text-emerald-700">{r.pod_count} file(s) · {r.pod_uploaded_by}</span>
                      : <span className="text-red-700">none</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {data?.can_upload && r.pod_count === 0 && (
                      <button className="h-6 px-2 text-[10px] border border-zinc-300 rounded-sm flex items-center gap-1 hover:bg-zinc-50"
                        onClick={() => startUpload(r.order_id)} data-testid={`pod-row-upload-${r.order_number}`}>
                        <Upload className="w-3 h-3" /> Upload
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={13} className="px-2 py-6 text-center text-zinc-400" data-testid="pod-empty">
                  {filters.missing_only ? "Every order in this window has a POD on file." : "No orders in this window."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
